<template>
  <section class="lk-step">
    <h2 class="lk-q">Fast geschafft.</h2>
    <p class="lk-sub">Nur noch ein paar Details, dann kann es losgehen.</p>
    <div class="lk-social">
      <span class="lk-social-lbl">Schneller mit:</span>
      <button
        type="button"
        class="lk-soc apple"
        aria-label="Mit Apple registrieren"
        @click="$emit('social', 'apple')"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.21 0-1.45.59-2.23.43-3.06-.4C1.45 14.5 2.35 6.07 8.81 5.69c1.43.07 2.42.71 3.27.79 1.26-.27 2.46-1.04 3.81-.93 1.6.13 2.81.79 3.61 2.05-3.29 1.96-2.54 6.21.42 7.42-.59 1.55-1.36 3.08-2.85 5.26zM12 5.65c-.13-2.32 1.78-4.3 4-4.65.31 2.6-2.33 4.79-4 4.65z"
          />
        </svg>
      </button>
      <button
        type="button"
        class="lk-soc google"
        aria-label="Mit Google registrieren"
        @click="$emit('social', 'google')"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2.179C7.021 2.179 2.543 6.657 2.543 12.179s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.192z"
          />
        </svg>
      </button>
      <button
        type="button"
        class="lk-soc facebook"
        aria-label="Mit Facebook registrieren"
        @click="$emit('social', 'facebook')"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M13.5 21v-7.5h2.6l.4-3h-3v-1.9c0-.87.24-1.46 1.48-1.46H17V4.42a20.96 20.96 0 0 0-2.27-.12c-2.25 0-3.79 1.38-3.79 3.9V10.5H8.4v3h2.54V21h2.56z"
          />
        </svg>
      </button>
    </div>
    <div class="divider-or"><em>oder mit E-Mail</em></div>
    <div class="signup-field">
      <label
        class="field-label"
        for="lk-name"
      >Dein Name</label>
      <input
        id="lk-name"
        type="text"
        class="signup-input"
        :value="name"
        placeholder="z. B. Nora"
        maxlength="30"
        @input="$emit('update:name', $event.target.value)"
      />
      <div class="helper">So sehen dich andere auf Viluu.</div>
    </div>
    <div class="signup-field">
      <label
        class="field-label"
        for="lk-email"
      >E-Mail</label>
      <input
        ref="emailInput"
        id="lk-email"
        type="email"
        class="signup-input"
        :class="{ error: emailMsg.error }"
        :value="email"
        placeholder="du@beispiel.de"
        inputmode="email"
        autocomplete="email"
        @input="onEmailInput"
        @blur="emailShowFormatError = true"
      />
      <div class="helper">Wir nutzen sie für Login und wichtige Mitteilungen.</div>
      <div
        v-show="emailMsg.show"
        class="email-msg"
        :class="{ error: emailMsg.error }"
        aria-live="polite"
      >
        <span class="em-txt">{{ emailMsg.text }}</span>
        <button
          v-if="emailMsg.suggestion"
          type="button"
          class="em-fix"
          @click="applyEmailSuggestion"
        >
          {{ emailMsg.suggestion }}?
        </button>
      </div>
    </div>
    <div class="signup-field">
      <label
        class="field-label"
        for="lk-pw"
      >Passwort</label>
      <div class="pw-wrap">
        <input
          id="lk-pw"
          :type="showPassword ? 'text' : 'password'"
          class="signup-input"
          :value="password"
          placeholder="Mindestens 8 Zeichen"
          autocomplete="new-password"
          @input="$emit('update:password', $event.target.value)"
        />
        <button
          type="button"
          class="pw-toggle"
          :aria-label="showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'"
          @click="showPassword = !showPassword"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
            <circle
              cx="12"
              cy="12"
              r="3"
            />
          </svg>
        </button>
      </div>
      <div
        class="strength"
        :class="'s' + pwScore"
        aria-hidden="true"
      >
        <span class="bar"></span><span class="bar"></span><span class="bar"></span>
        <span class="bar"></span>
      </div>
      <div class="helper">Mindestens 8 Zeichen.</div>
    </div>
    <label
      class="v-check req"
      style="margin-top: 12px"
    >
      <input
        type="checkbox"
        :checked="terms"
        @change="onTermsChange"
      />
      <span class="box">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M5 12l5 5L20 7" />
        </svg>
      </span>
      <span>Ich bestätige, dass ich 18 Jahre alt bin, die
        <a
          href="/agb"
          target="_blank"
          rel="noopener"
        >AGB</a>
        — insbesondere §1: Regelungen zur Nutzung des reinen Online-Unterhaltungsportals,
        sowie Bestimmungen zu Profilen und KI — vollständig gelesen und verstanden zu
        haben und akzeptiere diese sowie die
        <a
          href="/datenschutz"
          target="_blank"
          rel="noopener"
        >Datenschutzerklärung</a>.
      </span>
    </label>
    <p
      v-show="showTermsHint"
      class="helper lk-terms-hint"
      aria-live="polite"
    >
      Bitte bestätige noch die AGB.
    </p>
    <div
      class="submit-err"
      :class="{ show: submitError }"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
        />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <span>{{ submitError }}</span>
    </div>
    <span
      class="lk-submit-wrap"
      @pointerdown="onSubmitWrapPointerdown"
    >
      <button
        type="button"
        class="sb-next wide"
        :disabled="!canSubmit || submitting"
        @click="$emit('submit')"
      >
        Profil erstellen
        <svg
          class="ic-spark"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2.5 L13.6 9.2 L20 11 L13.6 12.8 L12 19.5 L10.4 12.8 L4 11 L10.4 9.2 Z" />
          <circle
            cx="19.5"
            cy="4.5"
            r="1.2"
          />
          <circle
            cx="4.5"
            cy="19.5"
            r="1"
          />
        </svg>
      </button>
    </span>
    <p class="lk-reassure">
      <svg
        class="lk-lock"
        viewBox="0 0 24 24"
        stroke="url(#ksg)"
        aria-hidden="true"
      >
        <use href="#ico-lock" />
      </svg>
      Sichere Übertragung · Kein Abo
    </p>
  </section>
</template>

<script>
import { passwordStrengthScore } from '../../signup/helpers';
import {
  isDisposable,
  isFunnelEmail,
  isPasswordOk,
  suggestEmail,
} from '../kennenlernenLeadFunnel';

export default {
  name: 'LeadFunnelStepSignup',
  props: {
    name: { type: String, default: '' },
    email: { type: String, default: '' },
    password: { type: String, default: '' },
    terms: { type: Boolean, default: false },
    canSubmit: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    submitError: { type: String, default: '' },
  },
  data() {
    return {
      emailShowFormatError: false,
      showPassword: false,
      showTermsHint: false,
    };
  },
  computed: {
    emailMsg() {
      const v = this.email;
      if (!v) {
        return { show: false, error: false, text: '', suggestion: null };
      }
      if (isDisposable(v)) {
        return {
          show: true,
          error: true,
          text: 'Wegwerf-Adressen sind leider nicht erlaubt — bitte nutze deine echte E-Mail.',
          suggestion: null,
        };
      }
      const suggestion = suggestEmail(v);
      if (suggestion) {
        return { show: true, error: false, text: 'Meintest du ', suggestion };
      }
      if (this.emailShowFormatError && !isFunnelEmail(v)) {
        return {
          show: true,
          error: true,
          text: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          suggestion: null,
        };
      }
      return { show: false, error: false, text: '', suggestion: null };
    },
    pwScore() {
      return passwordStrengthScore(this.password);
    },
    okExceptTerms() {
      return (
        (this.name || '').trim().length >= 2 &&
        isFunnelEmail(this.email) &&
        !isDisposable(this.email) &&
        isPasswordOk(this.password) &&
        !this.terms
      );
    },
  },
  methods: {
    onEmailInput(e) {
      this.emailShowFormatError = false;
      this.$emit('update:email', e.target.value.trim());
    },
    applyEmailSuggestion() {
      if (!this.emailMsg.suggestion) {
        return;
      }
      this.$emit('update:email', this.emailMsg.suggestion);
      this.emailShowFormatError = false;
      this.$nextTick(() => {
        if (this.$refs.emailInput) {
          this.$refs.emailInput.focus();
        }
      });
    },
    onTermsChange(e) {
      const checked = e.target.checked;
      this.$emit('update:terms', checked);
      if (checked) {
        this.showTermsHint = false;
      }
    },
    onSubmitWrapPointerdown() {
      if (this.canSubmit || this.submitting) {
        return;
      }
      if (this.okExceptTerms) {
        this.showTermsHint = true;
      }
    },
  },
};
</script>
